/** Local dev uses Vite proxy (`/api`). Staging/prod builds set VITE_API_URL in CI. */
const API_BASE = import.meta.env.VITE_API_URL?.replace(/\/$/, '') ?? '/api'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public fieldErrors: Record<string, string> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function parseApiErrorBody(
  body: unknown,
  statusText: string,
): { message: string; fieldErrors: Record<string, string> } {
  const fieldErrors: Record<string, string> = {}

  if (!body || typeof body !== 'object') {
    return { message: statusText, fieldErrors }
  }

  const record = body as Record<string, unknown>
  const message = typeof record.message === 'string' ? record.message.trim() : ''

  if (message) {
    return { message, fieldErrors }
  }

  if (record.success === false && record.error && typeof record.error === 'object') {
    const error = record.error as {
      issues?: Array<{ message?: string; path?: PropertyKey[] }>
    }

    for (const issue of error.issues ?? []) {
      if (!issue.message) continue
      const field = issue.path?.[0]
      if (typeof field === 'string' && !fieldErrors[field]) {
        fieldErrors[field] = issue.message
      }
    }

    const firstMessage = error.issues?.find((issue) => issue.message)?.message?.trim()
    return { message: firstMessage || statusText, fieldErrors }
  }

  return { message: statusText, fieldErrors }
}

export function getApiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (err instanceof ApiError) return err.message || fallback
  if (err instanceof Error) return err.message || fallback
  return fallback
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    credentials: 'include',
    ...options,
  })

  if (!res.ok) {
    const body = await res.json().catch(() => null)
    const { message, fieldErrors } = parseApiErrorBody(body, res.statusText)
    throw new ApiError(res.status, message, fieldErrors)
  }

  return res.json() as Promise<T>
}

/** For FormData bodies (file uploads) — omits Content-Type so fetch sets the multipart boundary. */
async function requestForm<T>(path: string, method: 'POST' | 'PATCH', body: FormData): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { method, body, credentials: 'include' })

  if (!res.ok) {
    const responseBody = await res.json().catch(() => null)
    const { message, fieldErrors } = parseApiErrorBody(responseBody, res.statusText)
    throw new ApiError(res.status, message, fieldErrors)
  }

  return res.json() as Promise<T>
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'DELETE',
      ...(body !== undefined && { body: JSON.stringify(body) }),
    }),
  postForm: <T>(path: string, body: FormData) => requestForm<T>(path, 'POST', body),
  patchForm: <T>(path: string, body: FormData) => requestForm<T>(path, 'PATCH', body),
}
